import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import {
  Upload,
  Paperclip,
  History,
  Download,
  CheckCircle2,
  X,
  FileText,
  FileUp,
  Image as ImageIcon,
  Loader2,
  User,
  Hash,
  Briefcase,
  Calendar,
  AlertCircle,
  Tag,
  Info
} from 'lucide-react';
import api, { getStorageUrl } from '../../api/axios';
import toast from 'react-hot-toast';
import CustomDatePicker from '../../components/Common/DatePicker/CustomDatePicker';
import styles from './EditDesignSubmission.module.css';
import NotFound from '../Error/NotFound';

const EditDesignSubmission = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submissionData, setSubmissionData] = useState(null);
  const [versionHistory, setVersionHistory] = useState([]);

  const [formData, setFormData] = useState({
    drawingType: 'Architectural Plans',
    version: '',
    revisionCode: '',
    submissionNotes: '',
    internalNotes: '',
    vendorReceivedDate: '',
    clientSubmissionDate: '',
    consultantSubmissionDate: ''
  });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [newFile, setNewFile] = useState(null);
  const fileInputRef = React.useRef(null);

  const fetchSubmissionData = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch details of the specific submission
      const response = await api.get(`/design-submissions/${id}`);
      if (response.data.success) {
        const data = response.data.data;
        setSubmissionData(data);
        setFormData({
          drawingType: data.drawing_type || 'Architectural Plans',
          version: data.version_number || '',
          revisionCode: data.revision_code || '',
          submissionNotes: data.submission_notes || '',
          internalNotes: data.notes || '',
          vendorReceivedDate: data.receive_from_vendor_date || data.vendor_received_date || '',
          clientSubmissionDate: data.submission_to_client_date || '',
          consultantSubmissionDate: data.submission_to_consultant_date || ''
        });

        // Also fetch history for this submission
        const historyResponse = await api.get(`/design-submissions/${id}/history`);
        const historyData = historyResponse.data.data || historyResponse.data || [];
        setVersionHistory(Array.isArray(historyData) ? historyData : []);
      }
    } catch (error) {
      console.error('Error fetching submission details:', error);
      toast.error('Failed to load submission details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (newFile) {
      const url = URL.createObjectURL(newFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [newFile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (name, date) => {
    setFormData(prev => ({
      ...prev,
      [name]: date ? date.toLocaleDateString('en-CA') : ''
    }));
  };

    const handleSubmit = async (e) => {
    e.preventDefault();

    setIsUpdating(true);
    const formDataObj = new FormData();
    formDataObj.append('_method', 'PUT'); // For Laravel to handle Multipart update
    formDataObj.append('version_number', formData.version);
    formDataObj.append('revision_code', formData.revisionCode);
    formDataObj.append('drawing_type', formData.drawingType);
    formDataObj.append('submission_notes', formData.submissionNotes);
    formDataObj.append('notes', formData.internalNotes);
    if (formData.vendorReceivedDate) {
      formDataObj.append('receive_from_vendor_date', formData.vendorReceivedDate);
    }
    if (formData.clientSubmissionDate) {
      formDataObj.append('submission_to_client_date', formData.clientSubmissionDate);
    }
    if (formData.consultantSubmissionDate) {
      formDataObj.append('submission_to_consultant_date', formData.consultantSubmissionDate);
    }
    
    if (newFile) {
      formDataObj.append('pdf_filepath', newFile);
    }

    try {
      const response = await api.post(`/design-submissions/${id}/edit`, formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success('Design submission updated successfully');
        fetchSubmissionData();
        setNewFile(null);
      }
    } catch (error) {
      console.error('Error updating design:', error);
      toast.error(error.response?.data?.message || 'Failed to update design');
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    fetchSubmissionData();
  }, [fetchSubmissionData]);

  const getStatusClass = (status) => {
    if (!status) return 'status-pending';
    switch (status.toLowerCase()) {
      case 'approved': return 'status-approved';
      case 'rejected': return 'status-rejected';
      case 'in_revision':
      case 'revision': return 'status-revision';
      case 'in_review': return 'status-pending';
      default: return 'status-pending';
    }
  };

  const handleDownload = async (submission) => {
    try {
      const response = await api.get(`/design-submissions/${submission.id}/file`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fileName = submission.pdf_filename || submission.pdf_filepath?.split('/').pop() || 'design.pdf';
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  if (loading) {
    return (
      <div className={styles['edit-design-container']}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div className={styles['loading-overlay']}>
            <Loader2 className={styles['spinner']} />
            <span>Loading submission details...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!submissionData) {
    return <NotFound />;
  }

  const loi = submissionData?.letter_of_intent;

  return (
    <div className={styles['edit-design-container']}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs customItems={[
            { label: 'Projects', path: '/projects' },
            { label: 'Design Submissions', path: '/projects/design-submissions' },
            { label: `Edit DS-${id}` }
          ]} />

          <div className={styles['page-header']}>
            <div className={styles['header-title']}>
              <h1>Edit Design Submission</h1>
              <p>Update technical drawing details or upload a new revision for review.</p>
            </div>
          </div>

          <div className={[styles['po-summary-card'], styles['card']].join(' ')}>
            <div className={styles['summary-info-grid']}>
              <div className={styles['summary-item']}>
                <label>LOI NUMBER</label>
                <div className={[styles['value'], styles['po-highlight']].join(' ')}>{loi?.loi_number || '---'}</div>
              </div>
              <div className={styles['summary-item']}>
                <label>VENDOR</label>
                <div className={styles['value']}>{submissionData?.vendor?.vendor_name || loi?.vendor?.vendor_name || '---'}</div>
              </div>
              <div className={styles['summary-item']}>
                <label>PROJECT</label>
                <div className={styles['value']}>{loi?.project?.project_name || '---'}</div>
              </div>
              <div className={styles['summary-item']}>
                <label>STATUS</label>
                <div className={styles['value']}>{submissionData?.status?.replace('_', ' ').toUpperCase() || '---'}</div>
              </div>
              <div className={styles['summary-item']}>
                <label>AMOUNT</label>
                <div className={styles['value']}>₹{parseFloat(loi?.total_amount || 0).toLocaleString('en-IN')}</div>
              </div>
              <div className={styles['summary-item']}>
                <label>ISSUE DATE</label>
                <div className={styles['value']}>{loi?.loi_date ? new Date(loi?.loi_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '---'}</div>
              </div>
            </div>
            <div className={styles['summary-visual']}>
              <div className={styles['drawing-frame']}>
                {loi?.loi_document_filepath ? (
                  loi.loi_document_filepath.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                    <div className={styles['existing-preview-wrapper']}>
                      <img
                       src={getStorageUrl(loi.loi_document_filepath)}
                        alt="Stored LOI Document"
                      />
                      <span className={styles['existing-badge']}>STORED IMAGE</span>
                    </div>
                  ) : (
                    <div className={styles['pdf-preview-small']}>
                      <FileText size={40} className={styles['pdf-icon-preview']} />
                      <span className={styles['pdf-label']}>STORED PDF</span>
                    </div>
                  )
                ) : (
                  <ImageIcon size={48} className={styles['placeholder-icon']} />
                )}
              </div>
            </div>
          </div>

          <div className={styles['upload-form-grid']}>
            <div className={[styles['form-section'], styles['card']].join(' ')}>
              <h3 className={styles['section-title']}>Design Details</h3>
              {/* <div className={styles['form-group']}>
                <label><Briefcase size={16} /> Drawing Type <span className={styles['required-star']}>*</span></label>
                <select
                  className={styles['design-input']}
                  name="drawingType"
                  value={formData.drawingType}
                  onChange={handleInputChange}
                >
                  <option>Architectural Plans</option>
                  <option>Structural Diagrams</option>
                  <option>Electrical Layouts</option>
                  <option>Plumbing Details</option>
                </select>
              </div> */}

              <div className={styles['form-row']}>
                <div className={styles['form-group']}>
                  <label><Tag size={16} /> Version/Revision <span className={styles['required-star']}>*</span></label>
                  <input
                    type="text"
                    className={styles['design-input']}
                    placeholder="e.g. 1.0"
                    name="version"
                    value={formData.version}
                    readOnly
                    style={{ backgroundColor: '#f1f5f9', cursor: 'not-allowed' }}
                  />
                </div>
                <div className={styles['form-group']}>
                  {/* <label><Info size={16} /> Revision Code <span className={styles['required-star']}>*</span></label>
                  <input
                    type="text"
                    className={styles['design-input']}
                    placeholder="e.g. REV-B"
                    name="revisionCode"
                    value={formData.revisionCode}
                    onChange={handleInputChange}
                  /> */}
                </div>
              </div>

              <div className={styles['form-group']}>
                <label><FileText size={16} /> Submission Notes</label>
                <textarea
                  className={styles['design-textarea']}
                  placeholder="Public notes for the reviewer regarding this revision..."
                  name="submissionNotes"
                  value={formData.submissionNotes}
                  onChange={handleInputChange}
                ></textarea>
              </div>

              <div className={styles['form-row']}>
                <div className={styles['form-group']}>
                  <label><Calendar size={16} /> Date receiving design from vendor <span className={styles['required-star']}>*</span></label>
                  <CustomDatePicker
                    selected={formData.vendorReceivedDate}
                    onChange={(date) => handleDateChange('vendorReceivedDate', date)}
                    placeholderText="Select date"
                  />
                </div>
                <div className={styles['form-group']}>
                  <label><Calendar size={16} /> Date of Submission to Client <span className={styles['required-star']}>*</span></label>
                  <CustomDatePicker
                    selected={formData.clientSubmissionDate}
                    onChange={(date) => handleDateChange('clientSubmissionDate', date)}
                    placeholderText="Select date"
                  />
                </div>
              </div>

              <div className={styles['form-row']}>
                <div className={styles['form-group']}>
                  <label><Calendar size={16} /> Date of Submission to Consultant</label>
                  <CustomDatePicker
                    selected={formData.consultantSubmissionDate}
                    onChange={(date) => handleDateChange('consultantSubmissionDate', date)}
                    placeholderText="Select date"
                  />
                </div>
                <div className={styles['form-group']}></div>
              </div>
              <div className={styles['form-group']}>
                <label><AlertCircle size={16} /> Internal Office Notes (Optional)</label>
                <textarea
                  className={styles['design-textarea']}
                  placeholder="Private internal notes for record keeping..."
                  name="internalNotes"
                  value={formData.internalNotes}
                  onChange={handleInputChange}
                ></textarea>
              </div>
            </div>

            <div className={[styles['form-section'], styles['card']].join(' ')}>
              <h3 className={styles['section-title']}>Files</h3>
              <div className={styles['form-group']}>
                <label><FileUp size={16} /> Main Design Document (PDF/Image)</label>
                <div
                  className={`${styles['upload-dropzone-large']} ${(newFile || submissionData?.pdf_filepath) ? styles['has-file'] : ''}`}
                  onClick={() => !newFile && fileInputRef.current.click()}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={(e) => {
                    e.preventDefault(); e.stopPropagation();
                    if (e.dataTransfer.files[0]) setNewFile(e.dataTransfer.files[0]);
                  }}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept=".pdf,image/*"
                    onChange={(e) => setNewFile(e.target.files[0])}
                  />

                  {newFile ? (
                    <div className={styles['preview-container-main']}>
                      {newFile.type === 'application/pdf' ? (
                        <div className={styles['pdf-preview-wrapper']}>
                          <iframe
                            src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                            className={styles['pdf-iframe']}
                            title="PDF Preview"
                          />
                          <div className={styles['file-overlay-info']}>
                            <FileText size={20} color="#ef4444" />
                            <span>{newFile.name} ({(newFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                          </div>
                        </div>
                      ) : newFile.type.startsWith('image/') ? (
                        <div className={styles['image-preview-wrapper-main']}>
                          <img src={previewUrl} alt="Design Preview" className={styles['image-preview-main']} />
                        </div>
                      ) : (
                        <div className={styles['file-generic-preview']}>
                          <FileUp size={48} />
                          <p>{newFile.name}</p>
                        </div>
                      )}

                      <div className={styles['preview-overlay-actions']}>
                        <button
                          type="button"
                          className={styles['remove-btn-overlay']}
                          onClick={(e) => { e.stopPropagation(); setNewFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                        >
                          <X size={18} />
                          <span>Remove Document</span>
                        </button>
                        <button
                          type="button"
                          className={styles['change-btn-overlay']}
                          onClick={(e) => { e.stopPropagation(); fileInputRef.current.click(); }}
                        >
                          <FileUp size={18} />
                          <span>Replace Document</span>
                        </button>
                      </div>
                    </div>
                  ) : submissionData?.pdf_filepath ? (
                    <div className={styles['design-preview-container']}>
                      {submissionData.pdf_filepath.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                        <img
                          src={getStorageUrl(submissionData.pdf_filepath)}
                          alt={submissionData.pdf_filename}
                          className={styles['design-preview-img']}
                        />
                      ) : (
                        <div className={styles['upload-icon-box']}>
                           <FileText size={48} color="#64748b" />
                        </div>
                      )}
                      
                      <div className={styles['preview-overlay-actions']}>
                        <button
                           type="button"
                           className={styles['change-btn-overlay']}
                           onClick={(e) => { e.stopPropagation(); handleDownload(submissionData); }}
                        >
                           <Download size={18} />
                           <span>Download Original</span>
                        </button>
                        <button
                          type="button"
                          className={styles['change-btn-overlay']}
                          style={{ backgroundColor: '#f59e0b', borderColor: '#f59e0b' }}
                          onClick={(e) => { e.stopPropagation(); fileInputRef.current.click(); }}
                        >
                          <FileUp size={18} />
                          <span>Upload Revision</span>
                        </button>
                      </div>
                      <div className={styles['filename-badge']} style={{position: 'absolute', bottom: '10px', left: '10px'}}>{submissionData.pdf_filename}</div>
                    </div>
                  ) : (
                    <>
                      <div className={styles['upload-icon-box']}>
                        <FileUp size={28} />
                      </div>
                      <div className={styles['upload-text']}>
                        <h4>Drag & drop new revision file here</h4>
                        <p>PDF or Images (JPG, PNG) supported</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

            </div>
          </div>

          <div className={styles['header-actions-btns']}>
            <button type="button" className={styles['btn-cancel']} onClick={() => navigate(-1)} disabled={isUpdating}>
              CANCEL
            </button>
            <button
              type="submit"
              className={styles['btn-primary']}
              onClick={handleSubmit}
              disabled={isUpdating}
            >
              {isUpdating ? <Loader2 className={styles['spinner']} size={18} /> : <CheckCircle2 size={18} />}
              <span>UPDATE SUBMISSION</span>
            </button>
          </div>

        </main>
      </div>
    </div>
  );
};

export default EditDesignSubmission;
